import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { intelligenceAPI, fetchAllPages, QualificationTemplate } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  FileText,
  Plus,
  Search,
  MoreVertical,
  Copy,
  Edit,
  Archive,
  CheckCircle,
  Clock,
  XCircle,
  Loader2,
  ClipboardList,
  Layers,
  ListChecks,
} from 'lucide-react';
import { toast } from 'sonner';
import { useConfirm } from '../contexts/ConfirmDialogContext';
import { useIsCustomer, useIsSupplier } from '../hooks/usePortalMode';
import { getApiErrorMessage } from '../utils/errorUtils';
import { templateTypeLabel } from '../lib/questionnaireLabels';

import TemplateNameCell from './qualification-templates/TemplateNameCell';
import { TYPE_CONFIG } from './qualification-templates/templateTypeConfig';

export default function QualificationTemplates() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [templates, setTemplates] = useState<QualificationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const isHelixOrCustomer = useIsCustomer();
  const isSupplier = useIsSupplier();

  // CR-7111: Suppliers should not access template management pages directly via URL
  useEffect(() => {
    if (isSupplier) {
      navigate('/questionnaires', { replace: true });
    }
  }, [isSupplier, navigate]);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      // CR-5395: do NOT pass filterType / filterStatus as API params.
      // Counts on the summary tiles need to reflect the full template
      // set regardless of which filter is active; filtering server-side
      // collapses Total/Active/Drafts/Archived to the size of the
      // currently-selected slice. Walk pages — the backend clamps
      // page_size, so one request capped the tiles at one page.
      const allTemplates = await fetchAllPages<QualificationTemplate>(
        (page) => intelligenceAPI.getTemplates({ page_size: 100, page }),
      );
      setTemplates(allTemplates);
    } catch (err: unknown) {
      console.error('Error loading templates:', err);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCloneTemplate = async (id: number, name: string) => {
    try {
      const cloneName = `${name} (Copy)`;
      await intelligenceAPI.cloneTemplate(id, cloneName);
      toast.success('Template cloned successfully');
      loadTemplates();
    } catch (err: unknown) {
      console.error('Error cloning template:', err);
      toast.error('Failed to clone template');
    }
  };

  const handlePublishTemplate = async (id: number) => {
    try {
      await intelligenceAPI.publishTemplate(id);
      toast.success('Template published successfully');
      loadTemplates();
    } catch (err: unknown) {
      console.error('Error publishing template:', err);
      toast.error(getApiErrorMessage(err, 'Failed to publish template'));
    }
  };

  const handleArchiveTemplate = async (id: number) => {
    try {
      await intelligenceAPI.archiveTemplate(id);
      toast.success('Template archived successfully');
      loadTemplates();
    } catch (err: unknown) {
      console.error('Error archiving template:', err);
      toast.error('Failed to archive template');
    }
  };

  const handleDeleteTemplate = async (id: number) => {
    const ok = await confirm({ title: 'Delete Template', description: 'Are you sure you want to delete this template?', confirmLabel: 'Delete', variant: 'destructive' });
    if (!ok) return;

    try {
      await intelligenceAPI.deleteTemplate(id);
      toast.success('Template deleted successfully');
      loadTemplates();
    } catch (err: unknown) {
      console.error('Error deleting template:', err);
      toast.error('Failed to delete template');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 border border-emerald-200 gap-1">
            <CheckCircle className="h-3 w-3" />Active
          </Badge>
        );
      case 'draft':
        return (
          <Badge className="bg-amber-50 text-amber-700 hover:bg-amber-50 border border-amber-200 gap-1">
            <Clock className="h-3 w-3" />Draft
          </Badge>
        );
      case 'archived':
        return (
          <Badge className="bg-gray-50 text-gray-600 hover:bg-gray-50 border border-gray-200 gap-1">
            <Archive className="h-3 w-3" />Archived
          </Badge>
        );
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getTypeBadge = (type: string) => {
    const config = TYPE_CONFIG[type];
    if (!config) return <Badge variant="secondary">{templateTypeLabel(type)}</Badge>;

    return (
      <Badge className={`${config.bg} ${config.text} hover:${config.bg} border-0 gap-1 font-medium`}>
        {config.icon}
        {config.label}
      </Badge>
    );
  };

  // Collapse version history: each template name has Issue 1/2/3 + Latest rows,
  // all marked active. The row that matters is the one assigned to new suppliers —
  // prefer version="Latest", otherwise the highest-numbered Issue. Historical
  // versions stay in the DB (answers reference them) but shouldn't clutter the list.
  const pickActiveVersion = (group: QualificationTemplate[]): QualificationTemplate => {
    const latest = group.find(t => String(t.version ?? '').toLowerCase() === 'latest');
    if (latest) return latest;
    const issueNumber = (t: QualificationTemplate) => {
      const match = String(t.version ?? '').match(/(\d+)/);
      return match ? parseInt(match[1], 10) : 0;
    };
    return [...group].sort((a, b) => issueNumber(b) - issueNumber(a))[0];
  };
  const templatesByKey = templates.reduce<Record<string, QualificationTemplate[]>>((acc, t) => {
    const key = `${t.name}::${t.template_type}`;
    (acc[key] ??= []).push(t);
    return acc;
  }, {});
  const activeTemplates = Object.values(templatesByKey).map(pickActiveVersion);

  // CR-5395: filter client-side so the summary-tile counts can stay
  // grounded in the full activeTemplates set above.
  const filteredTemplates = activeTemplates.filter(template => {
    if (filterStatus !== 'all' && template.status !== filterStatus) return false;
    if (filterType !== 'all' && template.template_type !== filterType) return false;
    const search = searchTerm.toLowerCase();
    if (!search) return true;
    return (
      template.name.toLowerCase().includes(search) ||
      (template.description?.toLowerCase().includes(search) ?? false)
    );
  });

  // Stats reflect the active (latest-per-name) view.
  const totalCount = activeTemplates.length;
  const activeCount = activeTemplates.filter((t) => t.status === 'active').length;
  const draftCount = activeTemplates.filter((t) => t.status === 'draft').length;
  const archivedCount = activeTemplates.filter((t) => t.status === 'archived').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[500px]">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto mb-3" />
          <p className="text-muted-foreground">Loading templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ClipboardList className="h-7 w-7 text-primary" />
            Qualification Templates
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage questionnaire templates for supplier qualification and compliance
          </p>
        </div>
        {isHelixOrCustomer && (
          <Button onClick={() => navigate('/intelligence/templates/new')} className="gap-2">
            <Plus className="h-4 w-4" />
            Create Template
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <button
          onClick={() => { setFilterStatus('all'); setFilterType('all'); }}
          className={`rounded-xl border p-4 text-left transition-all hover:shadow-md ${
            filterStatus === 'all' ? 'ring-2 ring-primary border-primary bg-primary/5' : 'bg-white hover:border-gray-300'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-gray-100">
              <FileText className="h-5 w-5 text-gray-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{totalCount}</div>
              <div className="text-xs text-muted-foreground font-medium">Total</div>
            </div>
          </div>
        </button>
        <button
          onClick={() => { setFilterStatus('active'); setFilterType('all'); }}
          className={`rounded-xl border p-4 text-left transition-all hover:shadow-md ${
            filterStatus === 'active' ? 'ring-2 ring-emerald-500 border-emerald-500 bg-emerald-50' : 'bg-white hover:border-gray-300'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-emerald-100">
              <CheckCircle className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-emerald-700">{activeCount}</div>
              <div className="text-xs text-muted-foreground font-medium">Active</div>
            </div>
          </div>
        </button>
        <button
          onClick={() => { setFilterStatus('draft'); setFilterType('all'); }}
          className={`rounded-xl border p-4 text-left transition-all hover:shadow-md ${
            filterStatus === 'draft' ? 'ring-2 ring-amber-500 border-amber-500 bg-amber-50' : 'bg-white hover:border-gray-300'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-amber-100">
              <Clock className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-amber-700">{draftCount}</div>
              <div className="text-xs text-muted-foreground font-medium">Drafts</div>
            </div>
          </div>
        </button>
        <button
          onClick={() => { setFilterStatus('archived'); setFilterType('all'); }}
          className={`rounded-xl border p-4 text-left transition-all hover:shadow-md ${
            filterStatus === 'archived' ? 'ring-2 ring-gray-400 border-gray-400 bg-gray-50' : 'bg-white hover:border-gray-300'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-gray-100">
              <Archive className="h-5 w-5 text-gray-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-700">{archivedCount}</div>
              <div className="text-xs text-muted-foreground font-medium">Archived</div>
            </div>
          </div>
        </button>
      </div>

      {/* Search & Type Filter */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search templates by name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 h-10"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-full md:w-[180px] h-10">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {Object.entries(TYPE_CONFIG).map(([value, config]) => (
              <SelectItem key={value} value={value}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Templates List */}
      {filteredTemplates.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="text-center py-16">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-gray-100 mb-4">
              <FileText className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold mb-2 text-gray-900">No templates found</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              {searchTerm || filterType !== 'all' || filterStatus !== 'all'
                ? 'No templates match your current filters. Try broadening your search.'
                : 'Get started by creating your first qualification template to assess and qualify suppliers.'}
            </p>
            {isHelixOrCustomer && !searchTerm && filterType === 'all' && filterStatus === 'all' && (
              <Button onClick={() => navigate('/intelligence/templates/new')} size="lg" className="gap-2">
                <Plus className="h-4 w-4" />
                Create Your First Template
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="shadow-sm">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50/80 hover:bg-gray-50/80">
                  <TableHead className="font-semibold text-gray-700">Template</TableHead>
                  <TableHead className="font-semibold text-gray-700">Type</TableHead>
                  <TableHead className="font-semibold text-gray-700">Status</TableHead>
                  <TableHead className="font-semibold text-gray-700">Structure</TableHead>
                  <TableHead className="font-semibold text-gray-700">Pass Score</TableHead>
                  <TableHead className="font-semibold text-gray-700">Version</TableHead>
                  <TableHead className="text-right font-semibold text-gray-700">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTemplates.map((template) => (
                  <TableRow
                    key={template.id}
                    className="cursor-pointer hover:bg-blue-50/50 transition-colors group"
                    onClick={() => navigate(`/intelligence/templates/${template.id}`)}
                  >
                    <TableCell className="py-4">
                      <TemplateNameCell template={template} />
                    </TableCell>
                    <TableCell>{getTypeBadge(template.template_type)}</TableCell>
                    <TableCell>{getStatusBadge(template.status)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3 text-sm">
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Layers className="h-3.5 w-3.5" />
                          {template.section_count || 0}
                        </span>
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <ListChecks className="h-3.5 w-3.5" />
                          {template.question_count || 0}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <div className="w-12 h-2 rounded-full bg-gray-200 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-primary"
                            style={{ width: `${template.pass_score}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-700">{template.pass_score}%</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Badge variant="outline" className="text-xs font-mono">
                          v{template.version}
                        </Badge>
                        {(() => {
                          const historyCount = (templatesByKey[`${template.name}::${template.template_type}`]?.length ?? 1) - 1;
                          return historyCount > 0 ? (
                            <span className="text-[10px] text-muted-foreground" title={`${historyCount} historical version${historyCount === 1 ? '' : 's'}`}>
                              +{historyCount}
                            </span>
                          ) : null;
                        })()}
                      </div>
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          <DropdownMenuItem onClick={() => navigate(`/intelligence/templates/${template.id}`)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          {isHelixOrCustomer && (
                            <>
                              <DropdownMenuItem onClick={() => handleCloneTemplate(template.id, template.name)}>
                                <Copy className="h-4 w-4 mr-2" />
                                Clone
                              </DropdownMenuItem>
                              {template.status === 'draft' && (
                                <DropdownMenuItem onClick={() => handlePublishTemplate(template.id)}>
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Publish
                                </DropdownMenuItem>
                              )}
                              {template.status === 'active' && (
                                <DropdownMenuItem onClick={() => handleArchiveTemplate(template.id)}>
                                  <Archive className="h-4 w-4 mr-2" />
                                  Archive
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => handleDeleteTemplate(template.id)}
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
